import { apiErrorMessage } from '@agos/api-client'
import {
  getGetSuperAdminFranchisesFranchiseIdIntegrationsQueryKey,
  useGetSuperAdminFranchises,
  useGetSuperAdminFranchisesFranchiseIdIntegrations,
  usePatchSuperAdminFranchisesFranchiseIdIntegrations,
} from '@agos/api-client/platform'
import { LANGUAGE_CATALOG, T, formatCount, tt } from '@agos/shared'
import { PageHeader, cn, EmptyState, toast, ReadFailed, Spinner, StatusBadge } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { KeyRound, Save } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useParams } from 'wouter'

import { registeredDomainsOf } from './customer-domain'
import { gpWebpayReturnLine } from './gp-webpay'
import {
  blankFieldNote,
  configuredCount,
  isSecretKind,
  formPatches,
  formValuesOf,
  integrationSectionsOf,
  integrationsOf,
  maskWarning,
  secretNote,
  settingGroupsOf,
  settingValueText,
  visibleSettingsOf,
  type SecretState,
  languageIsOn,
  languageToggled,
} from './integrations'

/**
 * A franchise's third-party credentials — shown as configured or not, never as values, and now
 * settable.
 *
 * The endpoint sends `{ "hasValue": true }` for each secret and nothing more. Three findings in this
 * repository (FB-132, FB-138, FB-143) are endpoints that handed a live key to a browser; this one
 * does not, and this page asks it for no more than it offers.
 *
 * **The write is `SAAS.md` §3.5, the owner's A14** — *"there are no functionality at all.. and we need
 * it 1:1 except design."* FB-505's line for this page was *"shows ten credentials as configured / not
 * configured and **cannot set one**"*. `PATCH …/integrations` existed with no caller.
 *
 * **A blank box means *unchanged*, never *clear*.** The browser was never given the current value, so
 * sending `""` would erase a working credential that nobody can read back to restore. `secretPayload`
 * omits blanks and `integrationPatches` nests what is left into the sections the endpoint branches
 * on — including turning `fiskal.<kasa>.apiKey` into a `kasaId`, because that middle segment names a
 * register and one call writes one row.
 */
export function FranchiseIntegrationsPage() {
  const params = useParams<{ id: string }>()
  const franchiseId = Number(params.id ?? 0)
  const enabled = Number.isFinite(franchiseId) && franchiseId > 0
  const { data, isLoading, isError } = useGetSuperAdminFranchisesFranchiseIdIntegrations(franchiseId, {
    query: { enabled },
  })
  const patch = usePatchSuperAdminFranchisesFranchiseIdIntegrations()
  // §3.190 - the franchise's customer domain, for the GP webpay card's return URL. The same list the franchise
  // screens read, so it is usually already cached.
  const franchises = useGetSuperAdminFranchises({ query: { enabled } })
  const returnLine = useMemo(() => {
    const rows = (franchises.data as { franchises?: unknown } | undefined)?.franchises
    const row = Array.isArray(rows)
      ? (rows.find(
          (one) =>
            typeof one === 'object' &&
            one !== null &&
            (one as Record<string, unknown>).id === franchiseId,
        ) as Record<string, unknown> | undefined)
      : undefined
    return gpWebpayReturnLine(registeredDomainsOf(row ?? null))
  }, [franchises.data, franchiseId])
  const queryClient = useQueryClient()
  const [typed, setTyped] = useState<Record<string, string>>({})

  const view = useMemo(() => integrationsOf(data), [data])
  // §3.57 - every scalar the endpoint answers, grouped by its section.
  const settings = useMemo(() => visibleSettingsOf(data), [data])
  const settingGroups = useMemo(() => settingGroupsOf(settings), [settings])
  // §3.86 - the form. `original` is what the endpoint answered for every editable field, `typed` is
  // what the operator changed, and only the difference travels.
  const sections = useMemo(() => integrationSectionsOf(data), [data])
  const original = useMemo(() => formValuesOf(data), [data])
  const secretByPath = useMemo(() => {
    const byPath: Record<string, SecretState> = {}
    for (const secret of view.secrets) byPath[secret.path] = secret
    return byPath
  }, [view])
  const bodies = useMemo(() => formPatches(original, typed), [original, typed])
  const warning = maskWarning(typed)

  /** A field the form shows and does not type - today only the auto-translate language list. */
  const readonlyText = (payload: unknown, path: string): string => {
    let current: unknown = payload
    for (const part of path.split('.')) {
      if (typeof current !== 'object' || current === null) return ''
      current = (current as Record<string, unknown>)[part]
    }
    if (Array.isArray(current)) return current.map((entry) => String(entry)).join(', ')
    return settingValueText(
      typeof current === 'string' || typeof current === 'number' || typeof current === 'boolean'
        ? current
        : null,
    )
  }

  const save = async () => {
    try {
      // In sequence rather than in parallel: two registers are two rows and the endpoint writes one
      // per call, so a failure on the second must not be reported as if the first had not happened.
      for (const body of bodies) {
        await patch.mutateAsync({ franchiseId, data: body })
      }
      await queryClient.invalidateQueries({
        queryKey: getGetSuperAdminFranchisesFranchiseIdIntegrationsQueryKey(franchiseId),
      })
      setTyped({})
      toast({ title: tt('Kľúče uložené') })
    } catch (failure) {
      // The typed values stay exactly as entered. A refused save that clears the boxes loses a key
      // the operator cannot read back from anywhere.
      toast({
        title: apiErrorMessage(failure, tt('Uloženie zlyhalo')),
        variant: 'destructive',
      })
    }
  }

  if (isLoading) {
    return (
      <main className="flex justify-center py-16" data-integrations>
        <Spinner />
      </main>
    )
  }

  return (
    <main className="space-y-4" data-integrations>
      <PageHeader
        title={tt('Integrácie')}
        description={`${formatCount(configuredCount(view))} ${tt('z')} ${formatCount(view.secrets.length)} ${tt('kľúčov je nastavených')}`}
        icon={<KeyRound className="w-5 h-5" />}
      />
      <p className="sr-only" data-secret-summary>
        {formatCount(configuredCount(view))} <T>z</T> {formatCount(view.secrets.length)}{' '}
        <T>kľúčov je nastavených</T>
      </p>

      {/* §3.107 - §3.82's banner, in the directory its sweep never reached. */}
      {isError && (
        <ReadFailed marker="integrations-error"><T>Integrácie sa nepodarilo načítať.</T></ReadFailed>
      )}
      {view.secrets.length === 0 && !isError ? (
        <EmptyState
          icon={<KeyRound className="w-8 h-8" />}
          title={tt('Žiadne integrácie')}
          description={tt('Táto franšíza nemá žiadne kľúče na nastavenie.')}
        />
      ) : (
        <section
          className="space-y-3"
          data-secret-list
          data-integration-sections={String(sections.length)}
        >
          <p className="text-xs text-muted-foreground" data-blank-note>
            {blankFieldNote()}
          </p>

          {/* §3.86 - **the catalogue draws the form.** This was one flat list of the ten masked
            * secrets as monospace dotted paths, with the other 37 fields the endpoint answers in a
            * read-only dump below. Legacy renders the same data as ten labelled cards, and *what is
            * `cardPaymentSlot` and may I change it* is not a question a screen should make somebody
            * answer from a path.
            *
            * The sections come from what the response contains: `hikvision` is absent on the owner's
            * data and a card with eight empty boxes for a franchise with no camera is worse than no
            * card.
            *
            * **`data-secret` stays on secrets only.** `tower.spec.ts:531` pins its count to the
            * number of `{hasValue}` fields the endpoint names, which is the assertion that says no
            * credential is rendered anywhere. */}
          {sections.map((section) => (
            <div
              key={section.key}
              className="bg-card border border-border rounded-2xl overflow-hidden"
              data-integration-section={section.key}
            >
              <div className="px-5 py-3 border-b border-border bg-muted/20">
                <h2 className="font-bold text-foreground text-sm">
                  <T>{section.title}</T>
                </h2>
                {section.subtitle !== undefined && (
                  <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">
                    <T>{section.subtitle}</T>
                  </p>
                )}
                {/* §3.190 - how the card is filled in, in order, and where GP webpay will send customers back. */}
                {section.guide !== undefined && (
                  <ol
                    className="mt-2 list-decimal pl-5 space-y-0.5 text-[11px] text-muted-foreground"
                    data-integration-guide={section.key}
                  >
                    {section.guide.map((step) => (
                      <li key={step}>
                        <T>{step}</T>
                      </li>
                    ))}
                  </ol>
                )}
                {section.key === 'payments.gpwebpay' && (
                  <p className="mt-2 text-[11px] text-foreground" data-gp-return-url={returnLine.url ?? ''}>
                    <T>Návratová adresa, na ktorú GP webpay vráti zákazníka (AGOS ju posiela sám, netreba ju nikde zadávať):</T>{' '}
                    <span className="font-mono break-all">
                      {returnLine.url ?? returnLine.note}
                    </span>
                  </p>
                )}
              </div>

              <div className="divide-y divide-border">
                {section.fields.map((field) => {
                  const secret = secretByPath[field.path]
                  const isSecret = isSecretKind(field.kind)
                  return (
                    <div
                      key={field.path}
                      className="px-5 py-2.5 flex flex-wrap items-center gap-3"
                      data-field={field.path}
                      {...(isSecret
                        ? {
                            'data-secret': field.path,
                            'data-secret-configured': secret?.configured ? 'yes' : 'no',
                          }
                        : {})}
                    >
                      <label
                        className="text-xs text-foreground min-w-[13rem] flex-shrink-0"
                        htmlFor={`field-${field.path}`}
                      >
                        <T>{field.label}</T>
                      </label>

                      {isSecret && (
                        <span data-secret-state>
                          <StatusBadge
                            label={secretNote(secret ?? { path: field.path, configured: false })}
                            color={secret?.configured ? 'green' : 'zinc'}
                            size="sm"
                          />
                        </span>
                      )}

                      {field.kind === 'languages' ? (
                        /* §3.113 - legacy's grid of twelve toggles, which this screen drew as a
                         * read-only comma list. `TenantIntegrationsService.BASIC_FIELDS` has always
                         * accepted the field, so a super-admin could not turn Hungarian on for Šurany
                         * and nothing but this control was stopping them. */
                        <span className="flex flex-wrap gap-1" data-field-languages={field.path}>
                          {LANGUAGE_CATALOG.map((language) => {
                            const value = typed[field.path] ?? original[field.path] ?? ''
                            const chosen = languageIsOn(value, language.code)
                            return (
                              <button
                                key={language.code}
                                type="button"
                                data-field-language={language.code}
                                data-field-language-on={chosen ? 'yes' : 'no'}
                                aria-pressed={chosen}
                                onClick={() =>
                                  setTyped((current) => ({
                                    ...current,
                                    [field.path]: languageToggled(
                                      current[field.path] ?? original[field.path] ?? '',
                                      language.code,
                                    ),
                                  }))
                                }
                                className={cn(
                                  'px-2 py-1 rounded-lg border text-xs font-semibold',
                                  chosen
                                    ? 'border-primary/40 bg-primary/10 text-primary'
                                    : 'border-border text-muted-foreground',
                                )}
                              >
                                {language.flag} {language.label}
                              </button>
                            )
                          })}
                        </span>
                      ) : field.kind === 'choice' ? (
                        /* §3.190 - the gateway. A select of the values the server accepts, so a value it
                         * would refuse cannot be typed. */
                        <select
                          id={`field-${field.path}`}
                          data-field-input={field.path}
                          value={typed[field.path] ?? original[field.path] ?? ''}
                          onChange={(change) =>
                            setTyped((current) => ({ ...current, [field.path]: change.target.value }))
                          }
                          className="flex-1 min-w-[12rem] min-h-[36px] px-3 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20"
                        >
                          {(field.options ?? []).map((option) => (
                            <option key={option.value} value={option.value}>
                              {tt(option.label)}
                            </option>
                          ))}
                        </select>
                      ) : field.kind === 'pem' || field.kind === 'secret-pem' ? (
                        /* §3.190 - a key is many lines, which an `<input>` would join into one. The private
                         * one starts empty like any secret: the read never sent it, and blank is unchanged. */
                        <textarea
                          id={`field-${field.path}`}
                          rows={5}
                          spellCheck={false}
                          autoComplete="off"
                          aria-label={field.path}
                          {...(isSecret
                            ? { 'data-secret-input': field.path }
                            : { 'data-field-input': field.path })}
                          placeholder={
                            isSecret
                              ? tt('Vložte nový kľúč - celý blok od -----BEGIN po -----END')
                              : '-----BEGIN CERTIFICATE-----'
                          }
                          value={typed[field.path] ?? (isSecret ? '' : original[field.path] ?? '')}
                          onChange={(change) =>
                            setTyped((current) => ({
                              ...current,
                              [field.path]: change.target.value,
                            }))
                          }
                          className="flex-1 min-w-[16rem] px-3 py-2 rounded-xl bg-background border border-border text-[11px] font-mono outline-none focus:ring-2 focus:ring-primary/20"
                        />
                      ) : field.kind === 'readonly' ? (
                        <span
                          className="text-xs text-muted-foreground font-mono"
                          data-field-value={field.path}
                        >
                          {readonlyText(data, field.path)}
                        </span>
                      ) : field.kind === 'boolean' ? (
                        <button
                          type="button"
                          id={`field-${field.path}`}
                          data-field-input={field.path}
                          data-field-on={(typed[field.path] ?? original[field.path]) === 'true'}
                          onClick={() =>
                            setTyped((current) => ({
                              ...current,
                              [field.path]:
                                (current[field.path] ?? original[field.path]) === 'true'
                                  ? 'false'
                                  : 'true',
                            }))
                          }
                          className={cn(
                            'px-2.5 py-1 rounded-lg border text-xs font-bold',
                            (typed[field.path] ?? original[field.path]) === 'true'
                              ? 'border-primary/40 bg-primary/10 text-primary'
                              : 'border-border text-muted-foreground',
                          )}
                        >
                          {(typed[field.path] ?? original[field.path]) === 'true'
                            ? tt('Áno')
                            : tt('Nie')}
                        </button>
                      ) : (
                        <input
                          id={`field-${field.path}`}
                          // A secret is `password` so it does not sit readable on a shared screen
                          // while it is typed; everything else is already on this page in the clear.
                          type={isSecret ? 'password' : field.kind === 'number' ? 'number' : 'text'}
                          autoComplete={isSecret ? 'new-password' : 'off'}
                          aria-label={field.path}
                          {...(isSecret
                            ? { 'data-secret-input': field.path }
                            : { 'data-field-input': field.path })}
                          placeholder={isSecret ? tt('Nová hodnota') : ''}
                          value={typed[field.path] ?? (isSecret ? '' : original[field.path] ?? '')}
                          onChange={(change) =>
                            setTyped((current) => ({
                              ...current,
                              [field.path]: change.target.value,
                            }))
                          }
                          className="flex-1 min-w-[12rem] min-h-[36px] px-3 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20"
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}

          <div className="bg-card border border-border rounded-2xl px-5 py-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-secret-save
              disabled={bodies.length === 0 || warning !== '' || patch.isPending}
              onClick={() => void save()}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold disabled:opacity-40',
                'border-primary/40 bg-primary/10 text-primary',
              )}
            >
              <Save className="w-3.5 h-3.5" /> <T>Uložiť kľúče</T>
            </button>
            {patch.isPending && <Spinner />}
            {/* How many requests this will make, because two registers are two rows. */}
            <span className="text-[11px] text-muted-foreground" data-secret-pending>
              {String(bodies.length)}
            </span>
            {warning !== '' && (
              <p className="text-[11px] text-red-500" data-secret-mask-warning>
                {warning}
              </p>
            )}
          </div>
        </section>
      )}

      {/* §3.57, FB-642 - what this franchise is actually wired to. `TenantIntegrationsService`
          answers 48 keys and this screen showed 25; the 23 missing included `callbackUrl`,
          `returnUrl`, `testMode` and `tatrapayClientId`, which are the subjects of §10A.143 and
          §10A.144 - so somebody debugging a payment could not see which of them was set.

          Rendered from whatever the endpoint answers rather than from a list of field names, so the
          screen cannot fall behind the service. A credential arrives as `{hasValue}` and is
          excluded by that shape, which is why no secret can appear here. Read-only deliberately:
          the write map accepts these keys, but twenty inputs need twenty validation decisions and
          §3.57 says why that is a separate change. */}
      {settings.length > 0 && (
        <section
          className="bg-card border border-border rounded-2xl overflow-hidden"
          data-integration-settings
        >
          <div className="px-5 py-3 border-b border-border bg-muted/20">
            <h2 className="font-bold text-foreground text-sm">
              <T>Nastavenia prevádzky</T>
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              <T>Na čo je táto franšíza napojená. Len na čítanie - kľúče sú vyššie.</T>
            </p>
          </div>
          <div className="p-5 space-y-4">
            {settingGroups.map((group) => (
              <div key={group} data-setting-group={group}>
                <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
                  {group}
                </h3>
                <dl className="mt-1.5 grid gap-x-4 gap-y-1 sm:grid-cols-2">
                  {settings
                    .filter((setting) => setting.group === group)
                    .map((setting) => (
                      <div
                        key={setting.path}
                        className="flex items-baseline justify-between gap-3 border-b border-border/40 py-1"
                        data-setting={setting.path}
                      >
                        <dt className="text-xs text-muted-foreground truncate">{setting.key}</dt>
                        <dd
                          className="text-xs font-medium text-foreground text-right break-all"
                          data-setting-value={setting.path}
                        >
                          {settingValueText(setting.value)}
                        </dd>
                      </div>
                    ))}
                </dl>
              </div>
            ))}
          </div>
          <p className="sr-only" data-setting-count>
            {String(settings.length)}
          </p>
        </section>
      )}
    </main>
  )
}
